/**
 * The Workflows page's view modes, and the two pure rules that guard them.
 *
 * Relocated from `pages/agent-view/view-mode.ts` 2026-09-23 when Agent View
 * was deleted (canvas reached feature parity with it; see
 * `project_bobcat_engineering_branches` memory) — this module was never
 * page-specific, it is `builder.tsx`'s whole view-mode state machine, so it
 * moved out to its own top-level file rather than dying with the page. The
 * `'agent'` mode itself is gone along with the page.
 *
 * Separate from any one view's own component so both the toggle and the page
 * branch can be tested without rendering a design-system web component, and
 * so the fallback rule has ONE definition both read.
 */
export type WorkflowsViewMode = 'table' | 'test' | 'canvas' | 'files';

/** The mode the page falls back to. Shipped surface — always reachable. */
export const DEFAULT_VIEW_MODE: WorkflowsViewMode = 'table';

/** Which Bobcat flag, if any, each mode is gated behind. */
export interface ViewModeAccess {
  /** The Test View tab is on: it follows `bobcat-validate-stage`. */
  testView: boolean;
  /** The Build canvas is on: it is part of the master `bobcat-five-stage-nav`. */
  canvasView: boolean;
  /** Build's Files tab — only with `bobcat-five-stage-nav` on. */
  filesView?: boolean;
}

/**
 * What each Workflows view needs. The Test View tab follows Validate and not
 * the master (PSTUDIO-4636); the Build canvas and Files follow the master.
 */
export const buildViewModeAccess = ({
  fiveStageNav,
  validateStage,
}: {
  fiveStageNav: boolean;
  validateStage: boolean;
}): ViewModeAccess => ({
  testView: validateStage,
  canvasView: fiveStageNav,
  filesView: fiveStageNav,
});

/**
 * Parse the string a `SegmentedControl` change event carries.
 *
 * The event's `detail.value` is typed `string`, so this is a real parse and not
 * a cast. It replaces a binary `=== 'agent' ? 'agent' : 'table'` coercion that
 * was correct only while there were exactly two segments — with a third, that
 * form silently reported a Test View click as "table".
 *
 * An unrecognised value falls back rather than throwing: the control is chrome,
 * and a bad value should not take the page down.
 */
export const parseViewMode = (value: string): WorkflowsViewMode => {
  switch (value) {
    case 'table':
    case 'test':
    case 'canvas':
    case 'files':
      return value;
    default:
      return DEFAULT_VIEW_MODE;
  }
};

/**
 * The mode Build opens on when nothing asked for a specific one: the canvas
 * when it's available, otherwise the table. A jump to a node or cell asks for
 * the table explicitly (`tableStore.pendingBuildViewMode`), so it still lands
 * there.
 */
export const defaultBuildViewMode = (
  access: Pick<ViewModeAccess, 'canvasView'>
): WorkflowsViewMode => (access.canvasView ? 'canvas' : DEFAULT_VIEW_MODE);

/**
 * The mode the page should actually render, given what the flags allow.
 *
 * `viewMode` is component state, but it outlives a flag: a user sitting on Test
 * View when the flag is switched off — or a `pending` flag on a hard reload —
 * must not be left on a surface that is no longer permitted. Rather than let
 * each branch re-check its own flag (three places to forget one), the page
 * resolves the mode once and renders it.
 *
 * Falls back to `table` and never to another gated mode, so this cannot bounce
 * a user between two flagged surfaces.
 */
export const resolveEffectiveViewMode = (
  viewMode: WorkflowsViewMode,
  access: ViewModeAccess
): WorkflowsViewMode => {
  if (viewMode === 'test' && !access.testView) return DEFAULT_VIEW_MODE;
  if (viewMode === 'canvas' && !access.canvasView) return DEFAULT_VIEW_MODE;
  if (viewMode === 'files' && !access.filesView) return DEFAULT_VIEW_MODE;

  return viewMode;
};
